/**
 * M2 內容 → 行程推薦加權。
 *
 * ## 這一頁在調什麼
 *
 * 購票機「行程推薦」的排序分數。演算法是：
 *
 *   時間利用率 × 100 ＋ 商品數 × 6 − 單位時間成本 × 0.05 ＋ **本頁的分類加權**
 *
 * 只取分數最高的前三組。這一頁能動的只有最後那一項——前三項動了會整體改變排序
 * 邏輯，那屬於演算法調整而非營運設定。
 *
 * ## 為什麼套票預設加 12 分
 *
 * 「商品數 × 6」讓兩件商品的組合固定比單一商品多 6 分。套票本質上就是一個組合，
 * 卻因為算「一件商品」而拿不到那個加分：
 *
 *   獨木舟 + 巡禮（120 分鐘）    111.4
 *   港灣暢遊雙體驗（120 分鐘）   105.4   ← 內容一樣，只因為是一件商品
 *
 * 12 分足以蓋過這 6 分的落差並留有餘裕。改成 0 就會回到「套票永遠排不進前三」。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../components/Button';
import {
  listRecommendationWeights,
  updateRecommendationWeights,
  type WeightMap,
} from '../lib/api-client';

const CATEGORIES: Array<{ key: keyof WeightMap; label: string; hint: string }> = [
  { key: 'package', label: '套裝（package）', hint: '套票；預設加權，理由見下方說明' },
  { key: 'trip', label: '行程（trip）', hint: '搭船遊港的各航程' },
  { key: 'experience', label: '體驗（experience）', hint: 'SUP、水上自行車等水上設施' },
  { key: 'charter', label: '包船（charter）', hint: '整船包場' },
];

const EMPTY: WeightMap = { package: 0, trip: 0, experience: 0, charter: 0 };

export function RecommendationWeightsPage(): JSX.Element {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['recommendation-weights'],
    queryFn: listRecommendationWeights,
  });

  const [form, setForm] = useState<WeightMap>(EMPTY);
  const [dirty, setDirty] = useState(false);

  // 載入後填入表單；使用者改過就不再覆蓋，免得背景重取把輸入洗掉
  useEffect(() => {
    if (!query.data || dirty) return;
    const next = { ...EMPTY };
    for (const w of query.data) next[w.category as keyof WeightMap] = w.bonus;
    setForm(next);
  }, [query.data, dirty]);

  const save = useMutation({
    mutationFn: () => updateRecommendationWeights(form),
    onSuccess: () => {
      setDirty(false);
      void qc.invalidateQueries({ queryKey: ['recommendation-weights'] });
    },
  });

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">行程推薦加權</h1>
      </div>

      <div
        style={{
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: 6,
          padding: 12,
          margin: '12px 0',
          fontSize: 13,
          maxWidth: 860,
        }}
      >
        購票機「行程推薦」會依分數取前三組。分數為
        <code> 時間利用率×100 ＋ 商品數×6 − 單位時間成本×0.05 ＋ 分類加權</code>，
        本頁調的是最後一項。<strong>加權是分數不是百分比</strong>，同一組行程裡每件商品的加權
        會相加。
      </div>

      {query.isPending && <p>載入中…</p>}
      {query.isError && <div className="error-banner">{(query.error as Error).message}</div>}

      {query.isSuccess && (
        <div className="table-card" style={{ maxWidth: 860 }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 220 }}>商品分類</th>
                <th style={{ width: 140 }}>加權</th>
                <th>說明</th>
              </tr>
            </thead>
            <tbody>
              {CATEGORIES.map((c) => (
                <tr key={c.key}>
                  <td>{c.label}</td>
                  <td>
                    <input
                      type="number"
                      step="1"
                      min={-100}
                      max={100}
                      value={form[c.key]}
                      aria-label={`${c.label} 加權`}
                      onChange={(e) => {
                        setDirty(true);
                        setForm((p) => ({ ...p, [c.key]: Number(e.target.value) }));
                      }}
                      style={{ width: 110, padding: 6 }}
                    />
                  </td>
                  <td style={{ color: '#6b7280' }}>{c.hint}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ padding: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
            <Button
              variant="primary"
              disabled={!dirty || save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? '儲存中…' : '儲存'}
            </Button>
            {save.isError && (
              <span style={{ color: '#b91c1c' }}>{(save.error as Error).message}</span>
            )}
            {save.isSuccess && !dirty && <span style={{ color: '#059669' }}>✓ 已儲存</span>}
          </div>
        </div>
      )}

      <div
        style={{
          background: '#fffbeb',
          border: '1px solid #fde68a',
          borderRadius: 6,
          padding: 12,
          margin: '16px 0',
          fontSize: 13,
          maxWidth: 860,
        }}
      >
        <b>套裝為什麼預設加 12：</b>「商品數×6」讓兩件商品的組合固定比單一商品多 6
        分。套票本質上就是一個組合，卻因為算成一件商品而拿不到那個加分——同樣 120
        分鐘、同樣內容，拆開賣的兩件會贏過套票。12 分蓋過這個落差並留有餘裕；
        <strong>改成 0 等於讓套票不會出現在推薦裡</strong>（除非顧客自己勾選「套裝」）。
      </div>
    </div>
  );
}
